import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { CircularProgress } from '@mui/material'
import {
    applySubscriptionCouponApi,
    currentSubscriptionDiscountApi,
    previewSubscriptionCouponApi,
    SubscriptionCouponInfo,
    SubscriptionDiscount,
    SubscriptionInvoicePreview,
} from '../../../../Service/membership'
import ReceivedGiftCards from '../../../../Modules/ReceivedGiftCards/ReceivedGiftCards'
import { getUserID } from '../../../../Service/getLocalStorage'
import './RedeemCoupon.scss'

type Preview = {
    coupon: SubscriptionCouponInfo
    next_invoice: SubscriptionInvoicePreview
    existing_discount: SubscriptionDiscount | null
    will_replace_existing: boolean
}

function formatCents(cents: number, currency = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
    }).format(cents / 100)
}

function formatDate(iso: string | null): string {
    if (!iso) return '—'
    try {
        return new Date(iso).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        })
    } catch {
        return iso
    }
}

function durationDescription(c: SubscriptionCouponInfo): string {
    switch (c.duration) {
        case 'once':
            return 'Applies to your next payment only.'
        case 'forever':
            return 'Applies to every renewal for as long as your subscription is active.'
        case 'repeating':
            return `Applies for the next ${c.duration_in_months ?? 'N'} month${c.duration_in_months === 1 ? '' : 's'}.`
        default:
            return ''
    }
}

const RedeemCoupon: React.FC = () => {
    const userId = useMemo(() => getUserID(), [])
    const [code, setCode] = useState('')
    const [preview, setPreview] = useState<Preview | null>(null)
    const [previewing, setPreviewing] = useState(false)
    const [applying, setApplying] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [currentDiscount, setCurrentDiscount] = useState<SubscriptionDiscount | null | undefined>(undefined)
    const [hasActiveSub, setHasActiveSub] = useState<boolean | undefined>(undefined)
    const [loadingCurrent, setLoadingCurrent] = useState(true)
    const [confirmReplace, setConfirmReplace] = useState(false)

    const loadCurrent = useCallback(async () => {
        if (!userId) {
            setLoadingCurrent(false)
            return
        }
        try {
            const res = await currentSubscriptionDiscountApi(userId)
            if (res?.data?.success && res.data.data) {
                setHasActiveSub(res.data.data.has_active_subscription)
                setCurrentDiscount(res.data.data.discount ?? null)
            } else {
                // Fail open: let the user try anyway rather than blocking them with
                // a "no membership" screen when the lookup itself errored.
                setHasActiveSub(true)
                setCurrentDiscount(null)
            }
        } catch {
            setHasActiveSub(true)
            setCurrentDiscount(null)
        } finally {
            setLoadingCurrent(false)
        }
    }, [userId])

    useEffect(() => {
        void loadCurrent()
    }, [loadCurrent])

    const resetPreview = () => {
        setPreview(null)
        setError(null)
        setConfirmReplace(false)
    }

    const handlePreview = async (e?: React.FormEvent) => {
        e?.preventDefault()
        if (!userId) {
            setError('You need to be logged in to redeem a code.')
            return
        }
        const trimmed = code.trim()
        if (!trimmed) {
            setError('Please enter a code.')
            return
        }
        resetPreview()
        setPreviewing(true)
        try {
            const res = await previewSubscriptionCouponApi(userId, trimmed)
            const body = res.data
            if (!body?.success || !body.data) {
                setError(body?.message || 'We could not validate that code.')
            } else {
                setPreview(body.data)
            }
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Could not reach the server. Please try again.')
        } finally {
            setPreviewing(false)
        }
    }

    const handleApply = async () => {
        if (!userId || !preview) return
        setApplying(true)
        try {
            const res = await applySubscriptionCouponApi(userId, preview.coupon.code, confirmReplace)
            const body = res.data
            if (body?.success) {
                toast.success(body.message || 'Discount added to your next payment.')
                setPreview(null)
                setCode('')
                setConfirmReplace(false)
                await loadCurrent()
            } else if (body?.requires_confirmation) {
                setConfirmReplace(true)
                setError(body.message || 'You already have a discount. Confirm to replace it.')
            } else {
                setError(body?.message || 'Could not apply the code. Please try again.')
            }
        } catch (err: any) {
            const data = err?.response?.data
            if (data?.requires_confirmation) {
                setConfirmReplace(true)
                setError(data.message || 'You already have a discount. Confirm to replace it.')
            } else {
                setError(data?.message || err?.message || 'Something went wrong. Please try again.')
            }
        } finally {
            setApplying(false)
        }
    }

    const handleGiftCardApply = (giftCode: string) => {
        setCode(giftCode)
        // run preview automatically after we update the input
        setTimeout(() => {
            void handlePreviewForCode(giftCode)
        }, 0)
    }

    const handlePreviewForCode = async (forCode: string) => {
        const trimmed = forCode.trim()
        if (!trimmed || !userId) return
        resetPreview()
        setPreviewing(true)
        try {
            const res = await previewSubscriptionCouponApi(userId, trimmed)
            const body = res.data
            if (!body?.success || !body.data) {
                setError(body?.message || 'We could not validate that code.')
            } else {
                setPreview(body.data)
            }
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Could not reach the server. Please try again.')
        } finally {
            setPreviewing(false)
        }
    }

    if (loadingCurrent) {
        return (
            <div className="redeem-coupon">
                <h2 className="redeem-coupon__title">Redeem a code</h2>
                <div className="redeem-coupon__card" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <CircularProgress size={18} />
                    <span style={{ color: '#5e7a86' }}>Loading your membership…</span>
                </div>
            </div>
        )
    }

    if (hasActiveSub === false) {
        return (
            <div className="redeem-coupon">
                <h2 className="redeem-coupon__title">Redeem a code</h2>
                <p className="redeem-coupon__sub">
                    Have a coupon or a gift card? Apply it to your next monthly payment here.
                </p>
                <div className="redeem-coupon__no-sub">
                    <strong>You don't have an active recurring membership yet.</strong>
                    <div className="mt-1">Once you sign up for a monthly or yearly plan, you can come back here to apply a code to your next renewal.</div>
                </div>
            </div>
        )
    }

    return (
        <div className="redeem-coupon">
            <h2 className="redeem-coupon__title">Redeem a code</h2>
            <p className="redeem-coupon__sub">
                Have a coupon or a gift card? Apply it to your next monthly payment. The discount lands on your next invoice automatically.
            </p>

            {currentDiscount && (
                <div className="redeem-coupon__card">
                    <h5 className="redeem-coupon__card-title">Currently applied</h5>
                    <div className="redeem-coupon__current-discount">
                        <div>
                            <div className="redeem-coupon__current-discount-label">
                                ✅ {currentDiscount.label}
                                {currentDiscount.name ? ` — ${currentDiscount.name}` : ''}
                            </div>
                            <div className="redeem-coupon__current-discount-meta">
                                {currentDiscount.duration === 'forever' && 'Applied to every renewal'}
                                {currentDiscount.duration === 'once' && 'Applied to your next invoice only'}
                                {currentDiscount.duration === 'repeating' &&
                                    `Applied for ${currentDiscount.duration_in_months ?? '?'} month${currentDiscount.duration_in_months === 1 ? '' : 's'}`}
                                {currentDiscount.end_at && ` · ends ${formatDate(currentDiscount.end_at)}`}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="redeem-coupon__card">
                <h5 className="redeem-coupon__card-title">Enter your code</h5>
                <form className="redeem-coupon__form" onSubmit={handlePreview}>
                    <input
                        type="text"
                        className="redeem-coupon__input"
                        placeholder="ENTER CODE"
                        value={code}
                        onChange={(e) => {
                            setCode(e.target.value)
                            if (preview) resetPreview()
                        }}
                        autoComplete="off"
                        disabled={previewing || applying}
                    />
                    <button
                        type="submit"
                        className="redeem-coupon__btn"
                        disabled={!code.trim() || previewing || applying}
                    >
                        {previewing ? <CircularProgress size={16} color="inherit" /> : 'Check code'}
                    </button>
                </form>
                {error && !preview && <div className="redeem-coupon__error">⚠️ {error}</div>}
            </div>

            {preview && (
                <div className="redeem-coupon__card">
                    <h5 className="redeem-coupon__card-title">Preview your next invoice</h5>

                    <div className="mb-2">
                        <span className="redeem-coupon__coupon-chip">
                            {preview.coupon.coupon_type === 'giftcard' ? '🎁 Gift card' : '🎟 Coupon'}
                        </span>
                        <strong>{preview.coupon.name || preview.coupon.code}</strong>
                        {preview.coupon.description && (
                            <span style={{ marginLeft: 6, color: '#5e7a86' }}>— {preview.coupon.description}</span>
                        )}
                    </div>

                    <div className="redeem-coupon__summary">
                        <div className="redeem-coupon__summary-row">
                            <span className="redeem-coupon__summary-row-label">Current next invoice</span>
                            <span className="redeem-coupon__summary-row-value redeem-coupon__summary-row-value--struck">
                                {formatCents(preview.next_invoice.total_before_cents, preview.next_invoice.currency)}
                            </span>
                        </div>
                        <div className="redeem-coupon__summary-row">
                            <span className="redeem-coupon__summary-row-label">After this code</span>
                            <span className="redeem-coupon__summary-row-value">
                                {formatCents(preview.next_invoice.total_after_cents, preview.next_invoice.currency)}
                            </span>
                        </div>
                        <div className="redeem-coupon__summary-row">
                            <span className="redeem-coupon__summary-row-label">You save</span>
                            <span className="redeem-coupon__summary-row-value redeem-coupon__summary-row-value--savings">
                                {formatCents(preview.next_invoice.savings_cents, preview.next_invoice.currency)}
                            </span>
                        </div>
                        <div className="redeem-coupon__summary-row">
                            <span className="redeem-coupon__summary-row-label">Charges on</span>
                            <span className="redeem-coupon__summary-row-value">
                                {formatDate(preview.next_invoice.next_payment_at || preview.next_invoice.period_end_at)}
                            </span>
                        </div>
                    </div>

                    <div className="redeem-coupon__duration-note">
                        ℹ️ {durationDescription(preview.coupon)}
                        {preview.coupon.coupon_type === 'giftcard' &&
                            preview.coupon.type === 'amount' &&
                            preview.next_invoice.savings_cents < preview.next_invoice.total_before_cents &&
                            ' Any unused gift card balance will not carry forward to future invoices.'}
                    </div>

                    {(preview.will_replace_existing || confirmReplace) && (
                        <div className="redeem-coupon__replace-warning">
                            ⚠️ You already have an active discount on this subscription. Applying this code will <strong>replace</strong> the current discount.
                        </div>
                    )}

                    {error && <div className="redeem-coupon__error">⚠️ {error}</div>}

                    <div className="redeem-coupon__actions">
                        <button
                            type="button"
                            className="redeem-coupon__btn redeem-coupon__btn--primary"
                            disabled={applying}
                            onClick={handleApply}
                        >
                            {applying ? (
                                <>
                                    <CircularProgress size={16} color="inherit" />
                                    Applying…
                                </>
                            ) : preview.will_replace_existing || confirmReplace ? (
                                'Replace discount & apply'
                            ) : (
                                'Apply to my next payment'
                            )}
                        </button>
                        <button
                            type="button"
                            className="redeem-coupon__btn redeem-coupon__btn--ghost"
                            disabled={applying}
                            onClick={resetPreview}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            <div className="redeem-coupon__gift-section">
                <h5 className="redeem-coupon__card-title" style={{ marginBottom: 6 }}>
                    🎁 Gift cards sent to you
                </h5>
                <p className="redeem-coupon__sub" style={{ marginBottom: 0 }}>
                    Tap <strong>Use</strong> on any gift card below to preview it against your next invoice.
                </p>
                <ReceivedGiftCards apply={handleGiftCardApply} coupon={code} />
            </div>
        </div>
    )
}

export default RedeemCoupon
